import { HttpHeaders, HttpParams } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { DebugModeService } from '@raxapp/debugging/debug-mode.service';
import { ActiveTenantLocationStore } from '@raxapp/multi-tenants/ActiveTenantLocation.store';

interface IRequestOptionsArgs {
    headers?: HttpHeaders;
    params?: HttpParams | Record<string, string | string[]>;
    reportProgress?: boolean;
    responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
    withCredentials?: boolean;
}

export enum RaxCustomHeadersKeys {
    ClientVersion = 'rax-client-version',
    ClientEnv = 'rax-client-env',
    ImpersonateId = 'rax-user-impersonate-id',
    ImpersonateUsername = 'rax-user-impersonate-username',
    AliasId = 'rax-user-alias-id',
    AliasUsername = 'rax-user-alias-username',
    LocaleId = 'rax-locale-id',
}

enum CustomHeadersKeys {
    TenantId = 'rax-tenant-id',
    LocationId = 'rax-location-id',
    DebugKey = 'rax-debug-key',
}

export class BookerPlannerBffClientBase {
    private readonly activeTenantLocationStore = inject(ActiveTenantLocationStore);
    private readonly debugModeService = inject(DebugModeService);

    public readonly raxCustomHeaders = new Map<RaxCustomHeadersKeys, string>();

    private readonly customHeaders = computed<Map<CustomHeadersKeys, string>>(() => {
        const customHeadersMap = new Map<CustomHeadersKeys, string>();

        if (this.activeTenantLocationStore.hasActiveTenant()) {
            customHeadersMap.set(CustomHeadersKeys.TenantId, this.activeTenantLocationStore.activeTenant()!.tenantId);
        }

        if (this.activeTenantLocationStore.hasActiveLocation()) {
            customHeadersMap.set(CustomHeadersKeys.LocationId, this.activeTenantLocationStore.activeLocation()!.locationId);
        }

        const debugKey = this.debugModeService.debugKey();
        if (debugKey) customHeadersMap.set(CustomHeadersKeys.DebugKey, debugKey);

        return customHeadersMap;
    });

    public InitializeHeaders(envName: string, version: string): void {
        this.SetClientInfo(envName, version);

        const impersonateId = localStorage.getItem(RaxCustomHeadersKeys.ImpersonateId);
        const impersonateUsername = localStorage.getItem(RaxCustomHeadersKeys.ImpersonateUsername);
        if (impersonateId && impersonateUsername) this.SetImpersionation(impersonateId, impersonateUsername);
    }

    public GetActiveAliasId(raxUserId: string): string | null {
        return localStorage.getItem(RaxCustomHeadersKeys.AliasId + raxUserId);
    }

    public SetAlias(raxUserId: string, userId: string, userName: string): void {
        if (!raxUserId) return;
        if (userId && userName) {
            localStorage.setItem(RaxCustomHeadersKeys.AliasId + raxUserId, userId);
            localStorage.setItem(RaxCustomHeadersKeys.AliasUsername + raxUserId, userName);
            this.raxCustomHeaders.set(RaxCustomHeadersKeys.AliasId, userId);
            this.raxCustomHeaders.set(RaxCustomHeadersKeys.AliasUsername, userName);
        } else {
            localStorage.removeItem(RaxCustomHeadersKeys.AliasId + raxUserId);
            localStorage.removeItem(RaxCustomHeadersKeys.AliasUsername + raxUserId);
            this.raxCustomHeaders.delete(RaxCustomHeadersKeys.AliasId);
            this.raxCustomHeaders.delete(RaxCustomHeadersKeys.AliasUsername);
        }
    }

    public SetImpersionation(userId: string, userName: string): void {
        if (userId && userName) {
            localStorage.setItem(RaxCustomHeadersKeys.ImpersonateId, userId);
            localStorage.setItem(RaxCustomHeadersKeys.ImpersonateUsername, userName);
            this.raxCustomHeaders.set(RaxCustomHeadersKeys.ImpersonateId, userId);
            this.raxCustomHeaders.set(RaxCustomHeadersKeys.ImpersonateUsername, userName);
        } else {
            localStorage.removeItem(RaxCustomHeadersKeys.ImpersonateId);
            localStorage.removeItem(RaxCustomHeadersKeys.ImpersonateUsername);
            this.raxCustomHeaders.delete(RaxCustomHeadersKeys.ImpersonateId);
            this.raxCustomHeaders.delete(RaxCustomHeadersKeys.ImpersonateUsername);
        }
    }

    private SetClientInfo(envName: string, version: string): void {
        this.raxCustomHeaders.set(RaxCustomHeadersKeys.LocaleId, 'nl-NL');
        this.raxCustomHeaders.set(RaxCustomHeadersKeys.ClientEnv, envName);
        this.raxCustomHeaders.set(RaxCustomHeadersKeys.ClientVersion, version);
    }

    protected transformOptions(options: IRequestOptionsArgs) {
        let headers = options.headers ?? new HttpHeaders();

        for (const [index, value] of this.raxCustomHeaders.entries()) {
            if (value) {
                headers = headers.append(index, value);
            }
        }

        for (const [index, value] of this.customHeaders().entries()) {
            if (value) {
                headers = headers.append(index, value);
            }
        }

        if (window.location.pathname) {
            headers = headers.append('rax-client-path', window.location.pathname);
        }

        options.headers = headers;
        return Promise.resolve(options);
    }
}
